import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Beaker, Check, Ban } from "lucide-react";
import { listSamplingFlagsForBooking, markSamplingFlag } from "@/lib/api";

const STATUS_STYLE = {
  pending:  "bg-amber-100 text-amber-700",
  sampled:  "bg-emerald-100 text-emerald-700",
  skipped:  "bg-slate-100 text-slate-500",
};

/**
 * Read-only display of sampling flags raised by lbs_evaluate_sampling_for_booking.
 * Lets QA staff mark each flag as "sampled" (done) or "skipped" (justified pass).
 */
export default function BookingSamplingPanel({ bookingId, currentUserId }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const r = await listSamplingFlagsForBooking(bookingId);
      if (cancelled) return;
      if (r.error) toast.error(r.error.message);
      setFlags(r.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [bookingId, tick]);

  if (loading) return null;
  if (flags.length === 0) return null;  // hide panel when nothing flagged

  const mark = async (id, status) => {
    const { error } = await markSamplingFlag(id, status, currentUserId);
    if (error) { toast.error(error.message); return; }
    toast.success(`Flag ${status}.`);
    setTick((k) => k + 1);
  };

  return (
    <div className="rounded-md border bg-amber-50/50" data-testid="booking-sampling-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-100">
        <Beaker className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-semibold">Flagged for sampling</span>
        <Badge variant="secondary" className="text-[10px]" data-testid="sampling-count">
          {flags.length}
        </Badge>
      </div>
      <div className="divide-y divide-amber-100">
        {flags.map((f) => (
          <div key={f.id} className="px-3 py-2" data-testid={`sampling-flag-${f.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Badge className={`text-[10px] font-mono ${STATUS_STYLE[f.status] || ""}`}>
                  {f.status}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{f.scope}</Badge>
                <span className="text-sm font-medium truncate">
                  {f.rule?.name || "(rule deleted)"}
                </span>
              </div>
              {f.status === "pending" && (
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline"
                    onClick={() => mark(f.id, "sampled")}
                    data-testid={`sampling-mark-sampled-${f.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Sampled
                  </Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => mark(f.id, "skipped")}
                    data-testid={`sampling-mark-skipped-${f.id}`}>
                    <Ban className="h-3.5 w-3.5 mr-1" /> Skip
                  </Button>
                </div>
              )}
            </div>
            {f.reason && <div className="text-xs text-slate-600 mt-1">{f.reason}</div>}
            {f.sampled_at && (
              <div className="text-[10px] text-slate-500 mt-1">
                Sampled {new Date(f.sampled_at).toLocaleString("en-AU", { hourCycle: "h23" })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
